// import { createContext, useContext, useState } from "react";
// import { useMsal } from "@azure/msal-react";

// const AppAuthContext = createContext(null);

// export const AppAuthProvider = ({ children }) => {
//     const { accounts } = useMsal();

//     // MSAL login
//     const msalLoggedIn = accounts.length > 0;

//     // DB login
//     const [dbLoggedIn, setDbLoggedIn] = useState(false);
//     const [dbUser, setDbUser] = useState(null); // 👈 NEW

//     const isLoggedIn = msalLoggedIn || dbLoggedIn;

//     const login = (type, payload) => {
//         if (type === "db") {
//             setDbLoggedIn(true);
//             setDbUser(payload); // 👈 store username here
//         }
//     };

//     const logout = () => {
//         setDbLoggedIn(false);
//         setDbUser(null);
//     };

//     return (
//         <AppAuthContext.Provider
//             value={{
//                 isLoggedIn,
//                 authType: msalLoggedIn ? "msal" : dbLoggedIn ? "db" : null,
//                 dbUser,
//                 login,
//                 logout,
//             }}
//         >
//             {children}
//         </AppAuthContext.Provider>
//     );
// };

// export const useAppAuth = () => {
//     const ctx = useContext(AppAuthContext);
//     if (!ctx) {
//         throw new Error("useAppAuth must be used within AppAuthProvider");
//     }
//     return ctx;
// };



import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { tokenManager } from "./tokenManager";
import { clearCache } from "./authService";

const AppAuthContext = createContext(null);

export const AppAuthProvider = ({ children }) => {
    const { accounts, instance } = useMsal();
    const [user, setUser] = useState(null);

    // 🔵 Azure login state
    const msalLoggedIn = accounts?.length > 0;
    const azureAccount = msalLoggedIn ? accounts[0] : null;

    // 🔵 Handle Azure login
    useEffect(() => {
        if (msalLoggedIn && azureAccount) {
            tokenManager.setLoginType("azure");

            setUser({
                type: "azure",
                username: azureAccount.username,
                email: azureAccount.idTokenClaims?.preferred_username,
                oid: azureAccount.idTokenClaims?.oid,
                name: azureAccount.name,
                raw: azureAccount,
            });
        }
    }, [msalLoggedIn, azureAccount]);

    // 🟢 Restore DB login from storage (only once)
    useEffect(() => {
        const loginType = tokenManager.getLoginType();

        if (loginType === "db") {
            try {
                const storedUser = localStorage.getItem("dbUser");
                if (storedUser) {
                    const parsed = JSON.parse(storedUser);
                    setUser({
                        type: "db",
                        username: parsed.username,
                        token: parsed.token,
                        raw: parsed,
                    });
                }
            } catch (err) {
                console.error("Failed to parse dbUser:", err);
                localStorage.removeItem("dbUser");
            }
        }
    }, []);

    const isLoggedIn = !!user;

    // 🔐 Login handler
    const login = useCallback((type, payload) => {
        if (type === "db") {
            tokenManager.setLoginType("db");

            const newUser = {
                type: "db",
                username: payload.username,
                token: payload.token,
                raw: payload,
            };

            setUser(newUser);
            localStorage.setItem("dbUser", JSON.stringify(payload));

            return { payload };
        }

        if (type === "azure" && accounts.length > 0) {
            tokenManager.setLoginType("azure");

            const account = accounts[0];
            const newUser = {
                type: "azure",
                username: account.username,
                email: account.idTokenClaims?.preferred_username,
                oid: account.idTokenClaims?.oid,
                name: account.name,
                raw: account,
            };

            setUser(newUser);
            return newUser;
        }

        return null;
    }, [accounts]);

    // 🚪 Logout handler
    const logout = useCallback(async () => {
        const loginType = tokenManager.getLoginType();

        if (loginType === "azure") {
            await instance.logoutPopup();
        }

        clearCache();
        tokenManager.clearAll();

        setUser(null);
        localStorage.removeItem("dbUser");

        window.location.replace("/login");
    }, [instance]);

        useEffect(() => {
        tokenManager.initialize();
    }, []);

    const contextValue = useMemo(() => ({
        isLoggedIn,
        authType: user?.type ?? null,
        login,
        logout,
        user,
    }), [isLoggedIn, user, login, logout]);

    return (
        <AppAuthContext.Provider value={contextValue}>
            {children}
        </AppAuthContext.Provider>
    );
};

export const useAppAuth = () => {
    const ctx = useContext(AppAuthContext);
    if (!ctx) {
        throw new Error("useAppAuth must be used within AppAuthProvider");
    }
    return ctx;
};
